import { useFormatter, useLocale, useTranslations } from "next-intl";
import React, { FC, useContext, useMemo } from "react";

import Label from "@/components/ui/Form/Label/Label";
import Icon from "@/components/ui/Icon/Icon";
import { SearchContext } from "@/context/SearchContext";
import { UserContext } from "@/context/UserContext";
import { PRODUCT_TYPE, SearchParams } from "@/types";

interface Props {
  label: string;
  asButton: boolean;
  reverse?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  filterKey?: string;
  filterName?: string;
  currentFilter?: string;
}
const FilterLabel: FC<Props> = ({ label, asButton, reverse = false, disabled, onClick, filterKey, filterName, currentFilter }) => {
  const t = useTranslations();
  const format = useFormatter();
  const locale = useLocale();
  const [language, region] = locale.split("-");
  const { counterParams, params } = useContext(SearchContext);
  const { currency, measurement } = useContext(UserContext);
  const disableClassName = `${disabled ? "pointer-events-none opacity-65 filter-disabled" : ""}`;

  function formatCompact(num: number, decimals = 2) {
    if (num === null || num === undefined || !isFinite(num)) return "";
    const abs = Math.abs(num);
    let value, suffix;

    if (abs >= 1_000_000_000) {
      value = abs / 1_000_000_000;
      suffix = "B";
    } else if (abs >= 1_000_000) {
      value = abs / 1_000_000;
      suffix = "M";
    } else if (abs >= 1_000) {
      value = abs / 1_000;
      suffix = "K";
    } else {
      return abs % 1 === 0 ? abs.toString() : abs.toFixed(decimals);
    }

    const formatted = value
      .toFixed(decimals)
      .replace(/\.0+$/, "")
      .replace(/(\.\d*[1-9])0+$/, "$1");

    return formatted + suffix;
  }

  const filterLabel: string = useMemo(() => {
    if (filterKey) {
      const currentParams = counterParams[filterKey as keyof SearchParams] || params[filterKey as keyof SearchParams];
      if (!!currentParams) {
        if (Array.isArray(currentParams)) {
          const cleanedParams = currentParams.filter((item) => !!item);
          switch (filterKey) {
            case PRODUCT_TYPE.PROPERTY_TYPE:
              return cleanedParams.length > 0
                ? cleanedParams.map((item) => t(`filter.label.${filterKey}-${item as string}`)).join(", ")
                : t(`filter.label.any`);
            case "price":
              if (cleanedParams.length === 2) {
                const minPrice = format.number(Math.floor((currentParams[0] as number) / 100), {
                  style: "currency",
                  // currency: currency,
                  trailingZeroDisplay: "stripIfInteger",
                });
                const maxPrice = format.number(Math.floor((currentParams[1] as number) / 100), {
                  style: "currency",
                  // currency: currency,
                  trailingZeroDisplay: "stripIfInteger",
                });
                return `${currency} ${formatCompact(Number(minPrice))} - ${formatCompact(Number(maxPrice))}`;
              }
              return t(`filter.label.any`);
            case "bedrooms":
              // {!["0"].includes(value as string) ? value : t("bedrooms.studio")}
              const values = cleanedParams.map((item) => {
                return item === "0" ? t("bedrooms.studio") : item;
              });
              return cleanedParams.length > 0 ? values.join(",") : t(`filter.label.any`);
              break;
            default:
              return (currentParams as string[]).join(", ");
          }
        } else {
          return currentParams.toString();
        }
      } else {
        return t(`filter.label.any`);
      }
    }
    return t(`filter.label.any`);
  }, [counterParams, params, t, filterKey]);
  return (
    <>
      {asButton ? (
        <div
          onClick={onClick && onClick}
          className="cursor-pointer flex items-center w-full justify-between h-full filter-label-container transition-colors"
        >
          <div className="leading-none">
            <Label
              className={`text-s font-secondary-light leading-none block filter-label ${reverse && !(currentFilter === filterName) ? "text-white" : ""}`}
              reverse={reverse}
            >
              {label}
            </Label>
            <span
              className={`${reverse && !(currentFilter === filterName) ? "text-white" : ""} text-nowrap mt-xxs leading-none ${language === "ru" ? "" : "text-m"}  block text-left font-medium filter-content transition-colors`}
              style={language === "ru" ? { fontSize: "14px" } : undefined}
            >
              {filterLabel}
            </span>
          </div>
          <Icon className={`${reverse ? "stroke-white" : "stroke-black"} mr-xs`} name="chevron-down" size="16" />
        </div>
      ) : disabled ? (
        <></>
      ) : (
        <p className={`font-primary text-s uppercase w-full px-m mt-m mb-xs ${disableClassName}`}>{label}</p>
      )}
    </>
  );
};
export default FilterLabel;
